import type { HTMLAttributes, PropsWithChildren } from "react";
import { forwardRef } from "react";

import { Card } from "@/components/Card/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";

const StatCard = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function StatCard({ className, ...props }, ref) {
    return (
      <Card
        ref={ref}
        className={cn(
          "p-6 overflow-hidden",
          "bg-neutral-fill shadow-md rounded-lg",
          "outline-solid outline-1 -outline-offset-1 outline-neutral-muted-border",
          className,
        )}
        {...props}
      />
    );
  },
);

const StatCardValueContainer = forwardRef<
  HTMLParagraphElement,
  PropsWithChildren<HTMLAttributes<HTMLParagraphElement>>
>(function StatCardValueContainer({ children }, ref) {
  return (
    <p ref={ref} className="flex items-baseline gap-2">
      {children}
    </p>
  );
});

const StatCardPrimaryValue = forwardRef<
  HTMLSpanElement,
  PropsWithChildren<HTMLAttributes<HTMLSpanElement>>
>(function StatCardPrimaryValue({ children }, ref) {
  return (
    <span
      ref={ref}
      className="whitespace-nowrap font-mono text-[32px] font-bold leading-[120%]"
    >
      {children}
    </span>
  );
});

const StatCardSecondaryValue = forwardRef<
  HTMLSpanElement,
  PropsWithChildren<HTMLAttributes<HTMLSpanElement>>
>(function StatCardSecondaryValue({ children }, ref) {
  return (
    <span ref={ref} className="whitespace-nowrap text-neutral-on-fill">
      {children}
    </span>
  );
});

const StatCardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function StatCardContent({ className, ...props }, ref) {
  return <div ref={ref} className={cn("py-1", className)} {...props} />;
});

const StatCardTable = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function StatCardTable({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        "grid grid-cols-[min-content_1fr_min-content] gap-x-3 gap-y-2",
        className,
      )}
      {...props}
    />
  );
});

const StatCardTableRow = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function StatCardTableRow({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        "col-span-3 grid h-8 grid-cols-subgrid items-center",
        className,
      )}
      {...props}
    />
  );
});

const StatCardTitle = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function StatCardTitle({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn("text-CardHeader leading-none capitalize", className)}
      {...props}
    />
  );
});

const StatCardHighlight = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(function StatCardHighlight({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn("py-4 grid place-items-center overflow-hidden", className)}
      {...props}
    />
  );
});

function StatCardTableHorizBar(props: { value: number; maxValue: number }) {
  const { value, maxValue } = props;
  const widthRatio = maxValue > 0 ? (value / maxValue) * 100 : 0;

  return (
    <div className="flex w-1/2 items-center">
      <div
        className="relative h-2.5 rounded-full bg-slate-400"
        style={{ width: `${widthRatio}%` }}
      >
        <div
          className={cn(
            "absolute right-0 top-1/2 -translate-y-1/2 translate-x-full pl-2 text-pSmall",
          )}
        >
          {props.value.toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function StatCardSkeleton({ className }: { className?: string }) {
  return <Skeleton className={cn("rounded-xl", className)} />;
}

export {
  StatCard,
  StatCardHighlight,
  StatCardValueContainer,
  StatCardPrimaryValue,
  StatCardSecondaryValue,
  StatCardContent,
  StatCardSkeleton,
  StatCardTable,
  StatCardTableHorizBar,
  StatCardTableRow,
  StatCardTitle,
};
